/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ENVIRONMENT, LocalStorageService } from '@iot-platform/core';
import { AiCredentialsDialogComponent, ChatbotSourceCommentComponent } from '@iot-platform/iot-platform-ui';
import { Chat, ChatCustomComponent, ChatEvent, ChatMessage, Environment, IotAction, UserAccount } from '@iot-platform/models/common';
import { LogType } from '@iot-platform/models/i4b';
import { get } from 'lodash';
import moment from 'moment';
import { catchError, expand, finalize, of, switchMap, takeWhile, tap, throwError, timer } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface PollingMessageSource {
  citationNumber: number;
  title: string;
  url: string;
}

interface PollingMessage {
  timestamp: number;
  from: LogType;
  message: string;
  messageId: string | null;
  sources?: PollingMessageSource[];
}

export enum PollingStatus {
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR'
}

interface PollingResponse {
  sessionId: string;
  messages?: PollingMessage[];
  timestamp: number;
  status: PollingStatus;
}

@Injectable({
  providedIn: 'root'
})
export class AiChatBotService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly environment: Environment = inject(ENVIRONMENT);
  private readonly localStorage: LocalStorageService = inject(LocalStorageService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly destroy = inject(DestroyRef);

  chat: Signal<Chat>;
  commentsLoading: WritableSignal<boolean> = signal(false);
  botName = signal(this.translateService.instant('AI_ASSISTANT.MESSAGES.BOT_NAME'));
  openingMessage = signal(this.translateService.instant('AI_ASSISTANT.MESSAGES.BOT_OPENING_MESSAGE'));

  chatMessages: WritableSignal<any[]> = signal([
    {
      type: LogType.USER,
      id: 'toto',
      icon: 'smart_toy',
      comment: this.openingMessage(),
      datetime: moment().toISOString(),
      user: { id: '', name: this.botName() }
    }
  ]);
  userSessionId: WritableSignal<string> = signal(undefined);
  customStyleForUserComment = { alignItems: 'flex-end', alignSelf: 'end' };

  constructor() {
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroy)).subscribe(() => {
      this.botName.set(this.translateService.instant('AI_ASSISTANT.MESSAGES.BOT_NAME'));
    });

    this.chat = computed(() => ({
      header: {
        title: signal(this.translateService.instant('AI_ASSISTANT.HEADER.TITLE')),
        icon: signal('smart_toy'),
        count: signal(undefined),
        loading: signal(false),
        backgroundColor: signal('#4f7ab7'),
        textColor: signal('#fff'),
        action: signal(undefined)
        /*
           action: signal({
             name: signal(IotAction.CONFIGURE),
             icon: signal('settings')
           })*/
      },
      body: {
        header: undefined,
        content: {
          messages: signal(
            this.chatMessages()?.map((comment): ChatMessage | ChatCustomComponent => {
              const customStyle = comment.type === LogType.USER.toUpperCase() ? this.customStyleForUserComment : null;
              if (!get(comment, 'url')) {
                return {
                  id: signal(comment.id),
                  text: signal(comment.comment),
                  author: signal(comment.user.name),
                  date: signal(comment.datetime),
                  icon: signal(comment.icon),
                  withActions: signal(false),
                  customStyle: signal(customStyle)
                };
              } else {
                return { ref: ChatbotSourceCommentComponent, inputs: { source: comment, customStyle } };
              }
            })
          ),
          emptyBodyLabel: signal('I18N_COMMENTS.NO_COMMENTS_FOUND'),
          loading: this.commentsLoading,
          conversationLoader: signal(true)
        }
      },
      footer: {
        label: signal('I18N_COMMENTS.INPUT_PLACEHOLDER'),
        backgroundColor: signal('#4f7ab7'),
        textColor: signal('#fff'),
        action: signal({
          name: signal(IotAction.ADD),
          icon: signal('send'),
          canDo: signal(true)
        })
      }
    }));
  }

  openCredentialsDialog(): void {
    this.dialog
      .open(AiCredentialsDialogComponent, { width: '600px' })
      .afterClosed()
      .subscribe((response) => this.localStorage.set('ai_chat_bot', JSON.stringify(response)));
  }

  addMessage(event: ChatEvent, currentUser: UserAccount, url: string) {
    this.commentsLoading.set(true);

    const context = AiChatBotService.getQuestionContextFromURL(url);
    const body = {
      userMessage: event.value,
      sessionId: this.userSessionId(),
      context
    };

    this.chatMessages.update((messages) => [...messages, this.formatUserMessage(event, currentUser)]);
    this.httpClient
      .post<PollingResponse>(`${this.environment.api.url_v2}/chat/prompt`, body)
      .pipe(
        tap((initialResponse: PollingResponse) => {
          this.userSessionId.set(initialResponse.sessionId);
        }),
        switchMap((response: PollingResponse) => {
          if (response.status === PollingStatus.RUNNING) {
            return this.pollChatMessages(response.sessionId, response.timestamp, currentUser);
          } else if (response.status === PollingStatus.COMPLETED) {
            return of(response);
          } else {
            console.error('Initial chat prompt returned an error or unexpected status:', response.status);
            return throwError(() => new Error('Initial chat prompt failed or unexpected status'));
          }
        }),
        finalize(() => {
          this.commentsLoading.set(false);
          console.log('Chat process (addMessage) finalized.');
        }),
        catchError((error) => {
          console.error('Initial request or polling stream error:', error);
          this.commentsLoading.set(false);
          return of(null);
        })
      )
      .subscribe((response: PollingResponse) => {
        this.userSessionId.set(response?.sessionId);
      });
  }

  formatUserMessage(event: ChatEvent, currentUser: UserAccount) {
    return {
      type: LogType.USER.toUpperCase(),
      comment: event.value,
      icon: 'person',
      user: { id: currentUser.id, name: `${currentUser.firstname} ${currentUser.lastname}` },
      datetime: moment().toISOString()
    };
  }

  private pollChatMessages(sessionId: string, initialTimestamp: number, currentUser: UserAccount) {
    const POLLING_DELAY_MS = 500; // delay between each poll

    return of({ status: PollingStatus.RUNNING, timestamp: initialTimestamp, sessionId } as PollingResponse).pipe(
      expand((response: PollingResponse) => {
        if (response.status === PollingStatus.RUNNING) {
          return timer(POLLING_DELAY_MS).pipe(
            switchMap(() =>
              this.httpClient.get<PollingResponse>(`${this.environment.api.url_v2}/chat/${sessionId}/messages?from=${response.timestamp}`).pipe(
                catchError((error) => {
                  console.error('Polling API error:', error);
                  const message: PollingMessage = {
                    timestamp: response.timestamp,
                    from: LogType.ASSISTANT,
                    message: this.translateService.instant('AI_ASSISTANT.ERRORS.POLLING_ERROR'),
                    messageId: null
                  };
                  return of({
                    status: PollingStatus.ERROR,
                    sessionId,
                    timestamp: response.timestamp,
                    messages: [message]
                  } as PollingResponse);
                })
              )
            )
          );
        } else {
          // If the status is no longer RUNNING, we stop the expand
          return of();
        }
      }),
      // handle the response and update chat messages
      tap((response: PollingResponse) => {
        if (response?.messages?.length) {
          const messages = response.messages.reduce((acc, message) => {
            const msg = this.formatMessage(message, currentUser);
            const sources = (message?.sources ?? []).reduce((acc1, item) => {
              const source = {
                comment: `${item.citationNumber} - ${item.title}`,
                url: item.url,
                datetime: moment.unix(response.timestamp).toISOString()
              };
              acc1.push(source);
              return acc1;
            }, []);
            acc = acc.concat(msg).concat(sources);
            return acc;
          }, []);

          this.chatMessages.update((chatMessages) => [...chatMessages, ...messages]);
        }
      }),
      // end the stream when the status is no longer RUNNING
      takeWhile((response: PollingResponse) => response.status === PollingStatus.RUNNING, true),

      catchError((error) => {
        console.error('Error in pollChatMessages stream:', error);
        return of(null); // allow the stream to complete gracefully
      })
    );
  }

  private formatMessage(msg: PollingMessage, currentUser: UserAccount) {
    const comment: string = msg.messageId ? this.translateService.instant(`AI_ASSISTANT.MESSAGES.MESSAGE_IDS.${msg.messageId}`) : msg.message;

    return {
      type: msg.from,
      comment,
      icon: msg.from.toUpperCase() === LogType.USER.toUpperCase() ? 'person' : 'smart_toy',
      user:
        msg.from.toUpperCase() === LogType.USER.toUpperCase()
          ? {
              id: currentUser.id,
              name: `${currentUser.firstname} ${currentUser.lastname}`
            }
          : { id: '', name: this.botName() },
      datetime: moment.unix(msg.timestamp).toISOString()
    };
  }

  static getQuestionContextFromURL(url: string): { conceptName: string; conceptId: string[] } | undefined {
    const urlParts = url.split('/').filter((part) => !!part);

    // Regex to detect UUID pattern: hexadecimal characters (0-9, a-f) and hyphens, but MUST contain at least one digit
    // Concept names contain only letters (a-z) and hyphens, never digits
    const isUuid = (part: string): boolean => /^[0-9a-f-]+$/i.test(part) && /\d/.test(part);

    let conceptName = '';
    const conceptId: string[] = [];

    for (const part of urlParts) {
      if (isUuid(part)) {
        // If it's a UUID and we don't have one yet, add it
        if (conceptId.length === 0) {
          conceptId.push(part);
        }
        // If we already have a UUID, ignore subsequent ones
      } else {
        // It's a concept name - keep the last one encountered
        if (part === 'home') {
          return;
        } else {
          conceptName = part.toUpperCase().replace('-', ' ');
          if (part === 'platform-event-rules') {
            conceptName = 'PO EVENT RULES';
          }
        }
      }
    }

    return { conceptName, conceptId };
  }
}
